import { protectPdfOutput } from "./protectPdfOutput";
import { jsPDF } from "jspdf";
import { loadPdfDocumentHeader, type PdfDocumentHeader } from "./PdfDocumentHeader";
import { registerC1PdfFonts } from "../../ConceptOne/src/lib/C1_PdfFonts";
import type { C1_PdfChapterArtifact } from "../../ConceptOne/src/lib/C1_PdfChapter";
import type { GroupAddressReport, GroupAddressUsage } from "./groupAddressReport";
import {
  measureGroupAddressLayout,
  groupAddressUsageValues,
  type GroupAddressLayout,
  type PdfTextColumn,
} from "./groupAddressLayout";

const cellPadding = 6;
const addressX = 45 + cellPadding;

/** Wraps all four usage columns and continues long evidence blocks on subsequent pages. */
function drawUsageDetails(
  doc: jsPDF,
  details: GroupAddressUsage[],
  startY: number,
  columns: PdfTextColumn[],
  nextPage: () => number,
): number {
  let y = startY;
  for (const detail of details) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const values = groupAddressUsageValues(detail);
    const lines: string[][] = values.map((value, index) => doc.splitTextToSize(value || "", columns[index].width));
    const lineCount = Math.max(1, ...lines.map((column) => column.length));
    let offset = 0;
    while (offset < lineCount) {
      if (y + 16 > 776) y = nextPage();
      const count = Math.min(lineCount - offset, Math.max(1, Math.floor((776 - y - 5) / 11)));
      doc.setFontSize(8);
      doc.setTextColor("#586a75");
      for (let index = 0; index < columns.length; index += 1) {
        const part = lines[index].slice(offset, offset + count);
        doc.setFont("helvetica", "normal");
        if (part.length) doc.text(part, columns[index].x, y, { baseline: "top", lineHeightFactor: 11 / 8 });
      }
      y += count * 11 + 5;
      offset += count;
    }
  }
  return y + 14;
}

/** Draws the shared document frame and repeated table headings. */
function drawTableHeader(doc: jsPDF, title: string, layout: GroupAddressLayout, header: PdfDocumentHeader) {
  header.draw(doc, { planType: "Gruppenadressen", hierarchy: [title, ""] });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#586a75");
  doc.text("Adresse", addressX, 114);
  doc.text("Name", layout.main[1].x, 114);
  if (layout.main[2]) doc.text("Verwendungen", layout.main[2].x, 114);
}

/** Renders the address hierarchy with wrapping, repeated headers and chapter navigation. */
export async function createGroupAddressPdf(
  report: GroupAddressReport,
  projectName: string,
): Promise<C1_PdfChapterArtifact> {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  await registerC1PdfFonts(doc);
  const layout = measureGroupAddressLayout(doc, report);
  const header = await loadPdfDocumentHeader();
  const pages: C1_PdfChapterArtifact["pages"] = [];
  const right = doc.internal.pageSize.getWidth() - 45;
  let y = 136;
  const newPage = (title: string, continuation = false, table = true) => {
    if (pages.length) doc.addPage();
    pages.push({ title: title + (continuation ? " · Fortsetzung" : ""), pageIndex: pages.length });
    if (table) drawTableHeader(doc, title, layout, header);
    else header.draw(doc, { planType: "Gruppenadressen", hierarchy: [title, ""] });
    y = 136;
  };
  for (const section of report.sections) {
    newPage(section.title);
    if (!section.rows.length) {
      doc.setFont("helvetica", "normal");
      doc.text("Keine Mittelgruppen vorhanden.", 45, y);
    }
    for (const row of section.rows) {
      doc.setFont("helvetica", row.level === 2 ? "bold" : "normal");
      doc.setFontSize(9);
      const lines: string[] = doc.splitTextToSize(row.name || "–", layout.main[1].width);
      let offset = 0;
      while (offset < lines.length) {
        if (y + 26 > 776) newPage(section.title, true);
        const count = Math.max(1, Math.floor((776 - y - 10) / 12));
        const part = lines.slice(offset, offset + count);
        const height = part.length * 12 + 10;
        const rowTop = y - 10;
        const rowCenter = rowTop + height / 2;
        const firstLineCenter = rowCenter - ((part.length - 1) * 12) / 2;
        if (row.level === 2) {
          doc.setFillColor("#edf1f4");
          doc.rect(45, rowTop, right - 45, height, "F");
        }
        doc.setTextColor("#172b3a");
        doc.setFont("helvetica", row.level === 2 ? "bold" : "normal");
        doc.setFontSize(9);
        doc.text(row.address, addressX, rowCenter, { baseline: "middle", lineHeightFactor: 12 / 9 });
        doc.text(part, layout.main[1].x, firstLineCenter, { baseline: "middle", lineHeightFactor: 12 / 9 });
        if (!report.detailedUsage && offset === 0)
          doc.text(String(row.usages), layout.main[2].x, rowCenter, { baseline: "middle", lineHeightFactor: 12 / 9 });
        y += height;
        offset += part.length;
      }
      if (row.usageDetails?.length) {
        y = drawUsageDetails(doc, row.usageDetails, y - 10, layout.usage, () => {
          newPage(section.title, true);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.text(`Verwendungen zu ${row.address}`, addressX, y);
          return y + 16;
        });
      }
    }
  }
  newPage("Übersicht", false, false);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const totals = report.totals;
  const summary = [
    `Hauptgruppen: ${totals.mainGroups}`,
    `Mittelgruppen: ${totals.middleGroups}`,
    `Gruppenadressen: ${totals.addresses}`,
    `Verwendungen: ${totals.usages}`,
  ];
  if (!report.sections.length) summary.unshift("Keine Gruppenadressen vorhanden.", "");
  doc.text(summary, 45, y, { lineHeightFactor: 1.8 });
  const date = new Intl.DateTimeFormat("de-DE").format(new Date());
  for (let index = 0; index < pages.length; index += 1) {
    doc.setPage(index + 1);
    doc.setDrawColor("#aeb9bf");
    doc.line(45, 794, right, 794);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor("#586a75");
    const projectLines: string[] = doc.splitTextToSize(`Projekt: ${projectName}`, 320);
    doc.text(projectLines[0] ?? "", 45, 808);
    doc.text(`${date} · ${index + 1} / ${pages.length}`, right, 808, { align: "right" });
  }
  return { blob: await protectPdfOutput(doc.output("blob"), "list"), pages };
}
